import { useFeatureIsOn } from '@growthbook/growthbook-react';
import { useQuery } from '@tanstack/react-query';
import { useAppConstantsContext } from '@/context/AppConstants';
import { serializedTypes } from '@/reports/types';
import { Record } from '@/search/useSearchPerson/Interfaces';
import { PersonByAddressTeaser } from '@/search/useSearchPerson/serializer/PersonByAddressTeaser';
import { PersonTeaser } from '@/search/useSearchPerson/serializer/PersonTeaser';
import { Nullable } from '@/utils/helperTypes';

export const PERSON_BY_ADDRESS_TEASER_ENDPOINT = 'hk/dd/v2/property/residents';
export const PERSON_TEASER_ENDPOINT = 'ms/hk/teaser';
const OPTOUT_PERSON_TEASER_ENDPOINT = 'hk/teaser';

export type SearchPersonProps = {
  searchPersonBy: string;
  options: {
    personSearchParams: PersonSearchParams | PersonByAddressSearchParams;
    isOptout?: boolean;
  };
};

export type PersonByAddressSearchParams = {
  address: string;
};

export type PersonSearchParams = {
  fName: string;
  lName: string;
} & Nullable<{
  mName: string;
  city: string;
  state: string;
  age: string;
  showPartialAddresses?: boolean;
}>;

export type PersonSearchParamsv2 = {
  fullName?: string;
  fName?: string;
  lName?: string;
  title?: string;
  company?: string | null;
  level?: string[];
  industry?: string;
  company_size_min?: string;
  company_size_max?: string;
  birthMonth?: string | null;
  birthYear?: string | null;
  deathMonth?: string | null;
  deathYear?: string | null;
  address?: string | null;
  mName?: string;
  city?: string;
  state?: string;
  age?: string;
  isDeceased?: boolean;
  location?: string;
};

type SearchConfig = {
  [key: string]: {
    teaserEndpoint: string;
    queryParams: string;
  };
};

async function searchPersonResults(
  { searchPersonBy, options }: SearchPersonProps,
  brandLink: string
) {
  const personParams = options.personSearchParams as PersonSearchParams;
  const personByAddressParams =
    options.personSearchParams as PersonByAddressSearchParams;

  const searchConfig: SearchConfig = {
    address: {
      teaserEndpoint: PERSON_BY_ADDRESS_TEASER_ENDPOINT,
      queryParams: `?address=${encodeURIComponent(
        personByAddressParams.address
      )}`,
    },
    name: {
      teaserEndpoint: options.isOptout
        ? OPTOUT_PERSON_TEASER_ENDPOINT
        : PERSON_TEASER_ENDPOINT,
      queryParams: `?age=${personParams.age}&city=${personParams.city}&exporttype=json&fn=${personParams.fName}&fname=${personParams.fName}&ln=${personParams.lName}&mn=${personParams.mName}&state=${personParams.state}`,
    },
  };
  const { teaserEndpoint, queryParams } =
    searchConfig[searchPersonBy || 'name'];

  brandLink = brandLink ? `${brandLink}/` : '';

  try {
    const headers = new Headers({
      'content-type': 'application/x-www-form-urlencoded',
    });

    const response = await fetch(
      `${brandLink}${teaserEndpoint}${queryParams}`,
      {
        method: 'GET',
        headers,
      }
    );

    if (response.ok) {
      return response.json();
    }
    // TODO: The error should be logged to OT
    throw new Error(`An unexpected error occurred. ${response}`);
  } catch (error: unknown) {
    // TODO: The error should be logged to OT
    throw new Error(`An unexpected error occurred. ${error}`);
  }
}

export function useSearchPerson(searchPersonProps: SearchPersonProps) {
  const {
    links: { brandLink },
  } = useAppConstantsContext();
  const isInPartialAddressesVariation = useFeatureIsOn('oar-1075');

  if (isInPartialAddressesVariation) {
    searchPersonProps.options.personSearchParams = {
      ...searchPersonProps.options.personSearchParams,
      showPartialAddresses: true,
    };
  }

  if (searchPersonProps.searchPersonBy === 'address') {
    return useQuery<serializedTypes.SerializedPropertyReport, Error>({
      queryKey: [
        'searchPersonByAddressResult',
        searchPersonProps.options.personSearchParams,
      ],
      queryFn: async () => {
        const searchPersonByAddressResult = await searchPersonResults(
          searchPersonProps,
          brandLink
        );

        return new PersonByAddressTeaser(searchPersonByAddressResult).data;
      },
    });
  }

  return useQuery<Record[], Error>({
    queryKey: [
      'searchPersonResult',
      searchPersonProps.options.personSearchParams,
    ],
    queryFn: async () => {
      const searchPersonResult = await searchPersonResults(
        searchPersonProps,
        brandLink
      );

      return new PersonTeaser(searchPersonResult).data;
    },
  });
}
